// BESS history for the УЗЕ tab: SoC (%) + power (kW) over today, chart + table.
// Pulls /forecasting/assets/:id/bess/day?date=YYYY-MM-DD (open endpoint).
import { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { API_BASE_URL } from '@/services/api';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';

interface Pt { time: string; soc: number | null; power: number | null; mode: string | null; temp: number | null; }

const todayKyiv = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Kyiv' }); // YYYY-MM-DD
const modeLabel = (m: string | null) => m === 'charging' ? 'Заряд' : m === 'discharging' ? 'Розряд' : m === 'idle' ? 'Очікує' : (m ?? '—');

export default function BessHistory({ assetId }: { assetId?: string }) {
  const [pts, setPts] = useState<Pt[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!assetId) { setErr('no-asset'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/forecasting/assets/${assetId}/bess/day?date=${todayKyiv()}`);
        if (!res.ok) throw new Error(String(res.status));
        const d = await res.json();
        const points: Pt[] = (d.points ?? []).map((p: any) => ({
          time: p.time,
          soc: p.battery?.socPct ?? null,
          power: p.pcs?.outputActivePowerKw ?? null,
          mode: p.status?.derivedPowerStatus ?? p.status?.powerFlow ?? null,
          temp: p.pcs?.innerTempC ?? null,
        }));
        if (!cancelled) setPts(points);
      } catch (e: any) {
        if (!cancelled) setErr(e.message || 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [assetId]);

  return (
    <div style={{ marginTop: 16 }}>
      <SectionCard title="Історія: SoC та потужність" subtitle="За поточну добу (Europe/Kyiv)">
        {err && <div style={{ color: scada.textMuted, fontSize: 13 }}>Історія недоступна для цієї станції.</div>}
        {!err && !pts && <div style={{ color: scada.textMuted, fontSize: 13 }}>Завантаження…</div>}
        {pts && pts.length === 0 && <div style={{ color: scada.textMuted, fontSize: 13 }}>Немає даних за сьогодні.</div>}

        {pts && pts.length > 0 && (
          <>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={pts} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
                  <CartesianGrid stroke={scada.border} strokeDasharray="3 3" />
                  <XAxis dataKey="time" tick={{ fill: scada.textMuted, fontSize: 11 }} stroke={scada.border} minTickGap={24} />
                  <YAxis yAxisId="p" tick={{ fill: scada.textMuted, fontSize: 11 }} stroke={scada.border}
                    label={{ value: 'кВт', angle: -90, position: 'insideLeft', fill: scada.textMuted, fontSize: 11 }} />
                  <YAxis yAxisId="soc" orientation="right" domain={[0, 100]} tick={{ fill: scada.textMuted, fontSize: 11 }} stroke={scada.border}
                    label={{ value: 'SoC %', angle: 90, position: 'insideRight', fill: scada.textMuted, fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: scada.text }} itemStyle={{ color: scada.textDim }} />
                  <Line yAxisId="soc" type="monotone" dataKey="soc" name="SoC, %" stroke={scada.state.curtailed} strokeWidth={2} dot={false} connectNulls />
                  <Line yAxisId="p" type="monotone" dataKey="power" name="Потужність, кВт" stroke={scada.state.ok} strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: 280, marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
                <thead>
                  <tr style={{ background: scada.panel2, position: 'sticky', top: 0 }}>
                    {['Час', 'SoC, %', 'Потужність, кВт', 'Режим', 'Темп., °C'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '7px 12px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...pts].reverse().map((p, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${scada.border}` }}>
                      <td style={{ padding: '6px 12px', color: scada.textDim, fontSize: 12 }}>{p.time}</td>
                      <td style={{ padding: '6px 12px', color: scada.text, fontSize: 12 }}>{p.soc != null ? p.soc.toFixed(1) : '—'}</td>
                      <td style={{ padding: '6px 12px', color: (p.power ?? 0) < 0 ? scada.state.curtailed : scada.state.ok, fontSize: 12 }}>{p.power != null ? p.power.toFixed(1) : '—'}</td>
                      <td style={{ padding: '6px 12px', color: scada.textDim, fontSize: 12 }}>{modeLabel(p.mode)}</td>
                      <td style={{ padding: '6px 12px', color: scada.textMuted, fontSize: 12 }}>{p.temp != null ? p.temp.toFixed(0) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}
